import { BAY_HIRE_QUERY_KEYS } from "@features/bay-hire/constants";
import {
  bayHireService,
  type BayHireRequest,
  type BayHireSearchFilter,
} from "@services/bayHireApi";
import { useQuery } from "@tanstack/react-query";

// DFT-0033/DFT-0069: filters (optional) power the Vendor request list's Filters panel - the
// generic BayHireSearchFilter[], built by useBayHireRequestList.tsx's buildFiltersFromUI.
export const useBayHireRequests = (
  pageNumber = 0,
  pageSize = 25,
  filters: BayHireSearchFilter[] = [],
) =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.requests, pageNumber, pageSize, filters],
    queryFn: () => bayHireService.listRequests(pageNumber, pageSize, filters),
  });

// Polls like the Security Clearance queue (ClearancesContainer.tsx) - the
// Operations queue is a multi-operator claim/unclaim workflow with the same
// eventual-consistency concerns (another operator's claim/unclaim/decision
// should surface without a manual refresh).
export const useBayHireQueue = (limit = 50) =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.queue, limit],
    queryFn: () => bayHireService.searchQueue({ limit }),
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    structuralSharing: false,
  });

// DFT-0015: offset-paginated variant of useBayHireQueue, additive alongside
// it. pageNumber is 0-based (matches the backend contract, same convention
// as useBayHireRequests).
//
// DFT-0027/DFT-0069 (Slice G): `filters` (optional) is the generic
// BayHireSearchFilter[] built by useBayHireOperationsQueue.tsx's buildFiltersFromUI,
// which includes a `status` entry (DFT-0040: OR's e.g. ["REQUESTED", "PAYED"] in one
// query, matched server-side, not merged client-side from separate paginated queries;
// Megaplan 2026-08-05 item 6 widened Ongoing to all non-waiting-list statuses). It is part
// of the queryKey - otherwise a cached page would be served unchanged when the user
// applies/clears a filter.
export const useBayHireQueuePaged = (
  pageNumber: number,
  pageSize: number,
  filters: BayHireSearchFilter[] = [],
) =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.queue, pageNumber, pageSize, filters],
    queryFn: () => bayHireService.searchQueuePaged({ filters, pageNumber, pageSize }),
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    structuralSharing: false,
  });

/**
 * DFT-0073: reads one Bay Hire request.
 *
 * <p><b>`poll` is opt-in per caller, and must stay that way.</b> Two components use this hook and
 * only one of them may refetch in the background:
 * <ul>
 *   <li>{@code BayHireRequestDetail} - a read-only page. It opts in, because the task state it
 *       renders ({@code currentTaskStatus}, which {@code BayHireFlowButtons} turns into
 *       Claim/Unclaim/decision visibility) can change with no user action at all: Task Management
 *       auto-unclaims after a staleness threshold. Without a poll the page keeps showing CLAIMED
 *       with Claim hidden until the operator happens to navigate or reload.</li>
 *   <li>{@code BayHireRequestFormPage} - an EDIT FORM, seeded from {@code initialData}. It must
 *       NOT opt in. A background refetch there would land new server data under a user who is
 *       mid-edit; any prefill keyed on the fetched request would then overwrite what they typed.
 *       This is the same hazard that makes the detail page pass {@code poll: !dialogOpen} while a
 *       dialog is open (see {@code usePreApproveDialog}, whose {@code form.reset(...)} effect is
 *       keyed on the request's dates).</li>
 * </ul>
 *
 * <p>Polling is only worth anything here because of DFT-0073 itself: task state is now resolved
 * LIVE by the BFF on every read. Before that it came from a persisted snapshot nothing corrected
 * on an auto-unclaim, so refetching returned the same stale CLAIMED however often you asked.
 *
 * <p>⚠️ Whatever the caller derives {@code poll} from must CHANGE BETWEEN RENDERS - React state,
 * not a ref. When {@code refetchInterval} resolves to {@code false} React Query clears the timer
 * and reconsiders it only when the query re-renders with new options; a ref mutation re-renders
 * nothing, so a pause would stick and the page would silently stop polling for good. An earlier
 * version used a ref and did exactly that - caught by
 * {@code useBayHireRequestByIdPolling.test.tsx}'s "resumes polling once the dialog closes".
 */
export const useBayHireRequestById = (
  id: string | null,
  initialData?: BayHireRequest | undefined,
  { poll = false }: { poll?: boolean } = {},
) =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.request, id],
    queryFn: () => (id ? bayHireService.getRequestById(id) : null),
    enabled: !!id,
    initialData,
    refetchInterval: poll ? 60_000 : false,
    refetchOnWindowFocus: poll,
  });

// DFT-0110: portCode (optional) scopes the catalogue to one port - the create form's
// BaySelectionSection always supplies it once a port is chosen, so a Shipping Agent is never
// offered a bay from the OTHER port. Part of the query key so switching port re-fetches rather
// than serving the other port's cached options.
export const useBayHireBayOptions = (portCode?: string) =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.bayOptions, portCode],
    queryFn: () => bayHireService.getBayOptions(portCode),
  });

// DFT-0044 slice F (FE-01/FE-02): the create form's read-only company card. staleTime is generous -
// a vendor's own company profile does not change mid-session, and this avoids one Reference Data
// round trip per form mount for data that cannot have changed.
export const useBayHireMyCompanyDetails = () =>
  useQuery({
    queryKey: [BAY_HIRE_QUERY_KEYS.myCompanyDetails],
    queryFn: () => bayHireService.getMyCompanyDetails(),
    staleTime: 5 * 60_000,
  });
